const DECIMAL_PATTERN = /^(0|[1-9]\d*)(?:\.(\d+))?$/;
let draftSequence = 0;
const draftId = () => `quotation-draft-${Date.now()}-${++draftSequence}`;

function pow10(scale) {
  return 10n ** BigInt(scale);
}

export function parseQuotationDecimal(value) {
  const text = String(value ?? "").trim();
  const match = DECIMAL_PATTERN.exec(text);
  if (!match) throw new Error("Enter a canonical non-negative amount.");
  const fraction = match[2] || "";
  return {
    units: BigInt(`${match[1]}${fraction}`),
    scale: fraction.length,
  };
}

export function decimalToCanonical({ units, scale }) {
  if (scale === 0) return units.toString();
  const padded = units.toString().padStart(scale + 1, "0");
  const whole = padded.slice(0, -scale);
  const fraction = padded.slice(-scale).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}

function align(left, right) {
  const scale = Math.max(left.scale, right.scale);
  return [
    left.units * pow10(scale - left.scale),
    right.units * pow10(scale - right.scale),
    scale,
  ];
}

export function addQuotationDecimals(leftValue, rightValue) {
  const [left, right, scale] = align(
    parseQuotationDecimal(leftValue),
    parseQuotationDecimal(rightValue)
  );
  return decimalToCanonical({ units: left + right, scale });
}

export function subtractQuotationDecimals(leftValue, rightValue) {
  const [left, right, scale] = align(
    parseQuotationDecimal(leftValue),
    parseQuotationDecimal(rightValue)
  );
  if (right > left) throw new Error("Quotation amount cannot be negative.");
  return decimalToCanonical({ units: left - right, scale });
}

export function multiplyQuotationDecimal(value, quantity) {
  if (!Number.isInteger(quantity) || quantity < 1) {
    throw new Error("Quantity must be a positive integer.");
  }
  const parsed = parseQuotationDecimal(value);
  return decimalToCanonical({
    units: parsed.units * BigInt(quantity),
    scale: parsed.scale,
  });
}

function divideRoundHalfUp(numerator, denominator) {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  return quotient + (remainder * 2n >= denominator ? 1n : 0n);
}

export function calculateQuotationLine(unitPrice, quantity) {
  const inclusive = multiplyQuotationDecimal(unitPrice, quantity);
  const parsed = parseQuotationDecimal(inclusive);
  const dppCents = divideRoundHalfUp(
    parsed.units * 10000n,
    111n * pow10(parsed.scale)
  );
  const dpp = decimalToCanonical({ units: dppCents, scale: 2 });
  return {
    inclusive_line_total: inclusive,
    line_dpp: dpp,
    line_ppn: subtractQuotationDecimals(inclusive, dpp),
  };
}

export function calculateQuotationDiscount(grossValue, percentageValue) {
  const gross = parseQuotationDecimal(grossValue);
  const percentage = parseQuotationDecimal(percentageValue);
  const numerator = gross.units * percentage.units;
  const denominator = 100n * pow10(gross.scale + percentage.scale);
  const roundedWhole = divideRoundHalfUp(numerator, denominator);
  const grossInWholeScale = gross.units;
  const roundedInGrossScale = roundedWhole * pow10(gross.scale);
  return roundedInGrossScale > grossInWholeScale
    ? decimalToCanonical(gross)
    : roundedWhole.toString();
}

export function calculateQuotationSummary(sections, shippingCost = "0") {
  let inclusive = "0";
  let dpp = "0";
  const sectionTotals = {};
  for (const section of sections || []) {
    let sectionDpp = "0";
    for (const item of section.items || []) {
      if (item.warning_type) continue;
      const line = calculateQuotationLine(
        item.inclusive_unit_price || "0",
        item.quantity
      );
      inclusive = addQuotationDecimals(inclusive, line.inclusive_line_total);
      dpp = addQuotationDecimals(dpp, line.line_dpp);
      sectionDpp = addQuotationDecimals(sectionDpp, line.line_dpp);
    }
    sectionTotals[section.service_ticket_id] = sectionDpp;
  }
  return {
    inclusive_item_total: inclusive,
    dpp,
    ppn: subtractQuotationDecimals(inclusive, dpp),
    shipping_cost: decimalToCanonical(parseQuotationDecimal(shippingCost || "0")),
    provisional_total: addQuotationDecimals(inclusive, shippingCost || "0"),
    section_dpp_totals: sectionTotals,
  };
}

export function createNeutralQuotationState() {
  return {
    state: "neutral",
    quotation: null,
    customer_source_service_ticket_id: "",
    customer: { name: "", pic: "", contact: "", address: "" },
    service_sections: [],
    serial_numbers: [],
    serial_error: "",
    complaint: "",
    resolution: "",
    items: [],
    shipping_cost: "0",
    dirty: false,
  };
}

export function addPpmSerialNumber(values, rawValue) {
  const value = String(rawValue ?? "").trim();
  if (!value) throw new Error("Serial Number cannot be empty.");
  const normalized = value.toLowerCase();
  if (values.some((current) => current.toLowerCase() === normalized)) {
    throw new Error("Serial Number must be unique.");
  }
  return [...values, value];
}

export function ticketToSection(ticket) {
  return {
    service_ticket_id: ticket.service_ticket_id,
    expected_service_ticket_updated_at: ticket.source_updated_at,
    service_ticket_number: ticket.service_ticket_number,
    serial_number: ticket.serial_number,
    model: ticket.model,
    customer_snapshot: ticket.customer,
    complaint: ticket.complaint || "",
    resolution: ticket.resolution || "",
    items: [
      ...(ticket.items || []).map((item) => ({ ...item, local_id: draftId() })),
      ...(ticket.orphan_warnings || []).map((warning) => ({
        ...warning,
        local_id: draftId(),
      })),
    ],
  };
}

export function quotationFormReducer(state, action) {
  switch (action.type) {
    case "ADD_TICKETS": {
      const existing = new Map(
        state.service_sections.map((section) => [
          section.service_ticket_id,
          section,
        ])
      );
      const sections = action.tickets.map(
        (ticket) =>
          existing.get(ticket.service_ticket_id) || ticketToSection(ticket)
      );
      const source =
        state.customer_source_service_ticket_id ||
        sections[0]?.service_ticket_id ||
        "";
      const sourceSection = sections.find(
        (section) => section.service_ticket_id === source
      );
      return {
        ...state,
        state: sections.length ? "ppa" : "neutral",
        service_sections: sections,
        customer_source_service_ticket_id: source,
        customer: sourceSection?.customer_snapshot || state.customer,
        dirty: true,
      };
    }
    case "REMOVE_TICKET": {
      const sections = state.service_sections.filter(
        (section) => section.service_ticket_id !== action.id
      );
      if (!sections.length) return createNeutralQuotationState();
      const removedSource =
        state.customer_source_service_ticket_id === action.id;
      return {
        ...state,
        service_sections: sections,
        customer_source_service_ticket_id: removedSource
          ? sections[0].service_ticket_id
          : state.customer_source_service_ticket_id,
        customer: removedSource ? sections[0].customer_snapshot : state.customer,
        dirty: true,
      };
    }
    case "SET_SOURCE": {
      const section = state.service_sections.find(
        (entry) => entry.service_ticket_id === action.id
      );
      return section
        ? {
            ...state,
            customer_source_service_ticket_id: action.id,
            customer: { ...section.customer_snapshot },
            dirty: true,
          }
        : state;
    }
    case "SET_FIELD":
      return { ...state, [action.field]: action.value, dirty: true };
    case "SET_CUSTOMER":
      return {
        ...state,
        customer: { ...state.customer, [action.field]: action.value },
        dirty: true,
      };
    case "SET_SECTIONS":
      return { ...state, service_sections: action.sections, dirty: true };
    case "START_PPM":
      return {
        ...createNeutralQuotationState(),
        state: "ppm",
        dirty: true,
      };
    case "ADD_SERIAL":
      try {
        return {
          ...state,
          serial_numbers: addPpmSerialNumber(
            state.serial_numbers,
            action.value
          ),
          serial_error: "",
          dirty: true,
        };
      } catch (error) {
        return { ...state, serial_error: error.message };
      }
    case "REMOVE_SERIAL":
      return {
        ...state,
        serial_numbers: state.serial_numbers.filter(
          (_, index) => index !== action.index
        ),
        serial_error: "",
        dirty: true,
      };
    case "SET_PPM_FIELD":
      return { ...state, [action.field]: action.value, dirty: true };
    case "LOAD":
      return { ...action.value, dirty: false };
    case "NEUTRAL":
      return createNeutralQuotationState();
    default:
      return state;
  }
}

export function hasUnresolvedQuotationWarnings(sections) {
  return (sections || []).some((section) =>
    (section.items || []).some((item) => item.warning_type === "orphan_usage")
  );
}

function itemPayload(item, patch = false) {
  if (item.warning_type) {
    throw new Error("Remove or replace unresolved Usage warnings before saving.");
  }
  if (item.item_type === "custom_cost") {
    if (!String(item.description || "").trim()) {
      throw new Error("Custom Cost description is required.");
    }
    parseQuotationDecimal(item.inclusive_unit_price);
    return {
      ...(patch && item.id ? { id: item.id } : {}),
      item_type: "custom_cost",
      origin: "manual",
      description: String(item.description || "").trim(),
      quantity: item.quantity,
      inclusive_unit_price: String(item.inclusive_unit_price || ""),
    };
  }
  const changedSelection = Boolean(item.expected_item_updated_at);
  if ((!patch || changedSelection || !item.id) && !(item.item_id || item.source_item_id)) {
    throw new Error("Select a valid Spare Part before saving.");
  }
  if ((!patch || changedSelection || !item.id) && !item.expected_item_updated_at) {
    throw new Error("Refresh the selected Spare Part before saving.");
  }
  return {
    ...(patch && item.id ? { id: item.id } : {}),
    item_type: "spare_part",
    origin: item.origin || "manual",
    ...(!patch || changedSelection || !item.id
      ? {
          item_id: item.item_id || item.source_item_id,
          expected_item_updated_at: item.expected_item_updated_at,
        }
      : {}),
    quantity: item.quantity,
  };
}

export function buildPpaCreatePayload(state) {
  return {
    creation_mode: "PPA",
    customer_source_service_ticket_id:
      state.customer_source_service_ticket_id,
    customer: { ...state.customer },
    service_sections: state.service_sections.map((section) => ({
      service_ticket_id: section.service_ticket_id,
      expected_service_ticket_updated_at:
        section.expected_service_ticket_updated_at,
      complaint: section.complaint,
      resolution: section.resolution,
      items: section.items.map((item) => itemPayload(item)),
    })),
    shipping_cost: state.shipping_cost,
  };
}

export function buildPpaPatchPayload(state) {
  return {
    expected_updated_at: state.quotation.updated_at,
    customer: { ...state.customer },
    service_sections: state.service_sections.map((section) => ({
      service_ticket_id: section.service_ticket_id,
      complaint: section.complaint,
      resolution: section.resolution,
      items: section.items.map((item) => itemPayload(item, true)),
    })),
    shipping_cost: state.shipping_cost,
  };
}

export function buildPpmCreatePayload(state) {
  if (!state.serial_numbers.length) {
    throw new Error("Add at least one Serial Number.");
  }
  return {
    creation_mode: "PPM",
    customer: { ...state.customer },
    serial_numbers: [...state.serial_numbers],
    complaint: String(state.complaint || "").trim(),
    resolution: String(state.resolution || "").trim(),
    items: state.items.map((item) => itemPayload(item)),
    shipping_cost: state.shipping_cost,
  };
}

export function buildPpmPatchPayload(state) {
  if (!state.serial_numbers.length) {
    throw new Error("Add at least one Serial Number.");
  }
  return {
    expected_updated_at: state.quotation.updated_at,
    customer: { ...state.customer },
    serial_numbers: [...state.serial_numbers],
    complaint: String(state.complaint || "").trim(),
    resolution: String(state.resolution || "").trim(),
    items: state.items.map((item) => itemPayload(item, true)),
    shipping_cost: state.shipping_cost,
  };
}

export function detailToQuotationState(quotation) {
  if (quotation.creation_mode === "PPM") {
    return {
      ...createNeutralQuotationState(),
      state: "ppm",
      quotation,
      customer: {
        name: quotation.customer?.name || "",
        pic: quotation.customer?.pic || "",
        contact: quotation.customer?.contact || "",
        address: quotation.customer?.address || "",
      },
      serial_numbers: [...(quotation.serial_numbers || [])],
      complaint: quotation.complaint || "",
      resolution: quotation.resolution || "",
      items: (quotation.items || []).map((item) => ({
        ...item,
        local_id: item.id || `saved-ppm-${item.item_name}`,
      })),
      shipping_cost: quotation.shipping_cost || "0",
      dirty: false,
    };
  }
  return {
    ...createNeutralQuotationState(),
    state: "ppa",
    quotation,
    customer_source_service_ticket_id:
      quotation.customer?.source_service_ticket_id || "",
    customer: {
      name: quotation.customer?.name || "",
      pic: quotation.customer?.pic || "",
      contact: quotation.customer?.contact || "",
      address: quotation.customer?.address || "",
    },
    service_sections: (quotation.service_sections || []).map((section) => ({
      ...section,
      model:
        section.model ||
        (section.items || []).find((item) => item.item_type === "spare_part")
          ?.model ||
        "",
      customer_snapshot: { name: quotation.customer?.name || "" },
      items: (section.items || []).map((item) => ({
        ...item,
        local_id: item.id || `saved-${section.service_ticket_id}-${item.item_name}`,
      })),
    })),
    shipping_cost: quotation.shipping_cost || "0",
    dirty: false,
  };
}
